import { ReactNode } from 'react';

import { Skeleton } from '@commenda/kakashi';
import { cn } from '@commenda/utils';

import { ServiceRequestTaskLabel } from '../../common/labels';
import { WorkflowSystemTask } from '../../common/tasks/workflow/WorkflowSystemTask';
import { ServiceRequestTaskCard } from '../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../types';
import { ServiceAuditRunStatusHelper } from '../helpers/ServiceAuditRunStatusHelper';
import { ServiceAuditWaitStepHelper } from '../helpers/ServiceAuditWaitStepHelper';
import { useServiceAuditStepStatus } from '../hooks/useServiceAuditStepStatus';
import { ServiceAuditTaskStatusIcon } from './ServiceAuditTaskStatusIcon';

interface IWorkflowServiceAuditWaitTaskProps extends TCommonTaskProp {
  auditorStepId: string;
}

/** The wait step after an auditor: reviewers see whether the audit failed or finished; others see the plain step. */
export const WorkflowServiceAuditWaitTask = ({
  task,
  context,
  isTimelineView,
  displayNumber,
  auditorStepId,
}: IWorkflowServiceAuditWaitTaskProps) => {
  const { role, workflowExecution } = context;
  const { isReviewerView, auditStatus, isLoading, error } =
    useServiceAuditStepStatus({
      auditorStepId,
      role,
      workflowExecution,
    });

  if (!isReviewerView) {
    return (
      <WorkflowSystemTask
        task={task}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
    );
  }

  const waitStepState =
    ServiceAuditWaitStepHelper.getWaitStepState(auditStatus);
  const statusLine: ReactNode = isLoading ? (
    <Skeleton className="h-4 w-32" />
  ) : error ? (
    <p className="txt-extra-small-regular text-text-error">
      {ServiceAuditRunStatusHelper.STATUS_LOAD_ERROR}
    </p>
  ) : (
    <p
      className={cn(
        'txt-extra-small-regular',
        ServiceAuditWaitStepHelper.WAIT_STEP_STATUS_CLASS_NAMES[waitStepState]
      )}
    >
      {ServiceAuditWaitStepHelper.WAIT_STEP_STATUS_LABELS[waitStepState]}
    </p>
  );
  const label: ReactNode = (
    <ServiceRequestTaskLabel label={task.title}>
      {statusLine}
    </ServiceRequestTaskLabel>
  );

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => (
      <ServiceAuditTaskStatusIcon
        auditStatus={auditStatus}
        taskStatus={status}
      />
    ),
    getStateConfig: () => ({ label, actions: null }),
  };

  return (
    <ServiceRequestTaskCard
      task={task}
      state={{ role, status: task.status }}
      config={config}
      context={context}
      isTimelineView={isTimelineView}
      displayNumber={displayNumber}
    />
  );
};
